import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';

const SEARCH_DEBOUNCE_MS = 500;
const SEARCH_LIMIT = 20;

/**
 * Searchable multi-select for employees (by name or employee_no).
 * Dropdown opens only when the user has typed a query; selections persist across searches.
 */
export default function EmployeeMultiSearchSelect({
  id,
  label,
  placeholder,
  mode = 'name',
  selected = [],
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (!debouncedSearch) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchEmployees = async () => {
      try {
        setLoading(true);
        const headers = await getAuthHeaders();
        const params = {
          skip: 0,
          limit: SEARCH_LIMIT,
          status: 'Active',
          sort_by: mode === 'employee_no' ? 'employee_no' : 'name',
          sort_order: 'asc',
          ...(mode === 'employee_no'
            ? { employee_no: debouncedSearch }
            : { name: debouncedSearch }),
        };
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/employees`,
          { headers, params }
        );
        if (cancelled) return;
        setOptions(response.data.employees || []);
      } catch (error) {
        if (!cancelled) {
          console.error('Error searching employees:', error);
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchEmployees();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, mode]);

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, closeMenu]);

  const selectedNos = new Set(
    selected.map((item) => item.employee_no).filter(Boolean)
  );

  const toggleEmployee = (employee) => {
    const employeeNo = employee.employee_no;
    if (!employeeNo) return;

    if (selectedNos.has(employeeNo)) {
      onChange(selected.filter((item) => item.employee_no !== employeeNo));
      return;
    }

    onChange([
      ...selected,
      {
        employee_no: employeeNo,
        name: employee.name || '',
      },
    ]);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange([]);
    setSearchTerm('');
    setDebouncedSearch('');
    setOptions([]);
    setOpen(false);
  };

  const handleInputFocus = () => {
    if (searchTerm.trim()) {
      setOpen(true);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setOpen(Boolean(value.trim()));
  };

  const optionLabel = (employee) => {
    const name = employee.name || '—';
    const no = employee.employee_no || '—';
    return mode === 'employee_no' ? `${no} — ${name}` : `${name} (${no})`;
  };

  return (
    <div ref={containerRef} className="w-full sm:max-w-xs relative">
      <div className="flex items-center justify-between gap-2 mb-1">
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-500">
            {selected.length} selected
          </span>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              className="text-xs font-semibold text-[#674936] hover:underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      <input
        id={id}
        type="search"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#674936]"
      />
      {open && searchTerm.trim() && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute left-0 right-0 z-40 mt-1 max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {loading && (
            <p className="px-3 py-2 text-sm text-gray-500">Searching...</p>
          )}
          {!loading && options.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-500">No employees found.</p>
          )}
          {!loading &&
            options.map((employee) => {
              const employeeNo = employee.employee_no || '';
              const checked = selectedNos.has(employeeNo);
              return (
                <button
                  key={employeeNo || employee.id || employee.employee_id}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggleEmployee(employee)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 ${
                    checked ? 'bg-gray-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    readOnly
                    checked={checked}
                    className="h-4 w-4 rounded border-gray-300 text-[#674936] pointer-events-none"
                    tabIndex={-1}
                  />
                  <span className="break-words">{optionLabel(employee)}</span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}
